
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { FaSearch, FaFileExcel, FaFilePdf, FaTimes } from "react-icons/fa";
import { format, subDays } from "date-fns";
import { useQuery } from '@tanstack/react-query'; 
import "./TripReport.css";

const API_BASE_URL = "https://api.yellowmatrix.co.in";

// Helper function to format date for API
const formatDateForAPI = (dateStr) => {
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
};

// --- Custom Hook for Available Vehicles ---
const useAvailableVehiclesQuery = () => {
  const fetchAvailableVehicles = async () => {
    const token = localStorage.getItem("token");
    if (!token) throw new Error("Authentication token not found. Please log in.");

    const response = await fetch(`${API_BASE_URL}/vehicles`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.Error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.map(v => v.VehicleID);
  };

  return useQuery({
    queryKey: ['availableVehicles'],
    queryFn: fetchAvailableVehicles,
    staleTime: Infinity, 
  });
};

// --- Custom Hook for Trip Data ---
const useTripDataQuery = (selectedVehicles, startDate, endDate, isEnabled) => {
  const fetchTripData = async () => {
    const token = localStorage.getItem("token");
    if (!token) throw new Error("Authentication token not found");
    if (selectedVehicles.length === 0) throw new Error("No vehicles selected");

    const vehicleIds = selectedVehicles.join(',');
    const formattedStartDate = formatDateForAPI(startDate);
    const formattedEndDate = formatDateForAPI(endDate);

    const dataUrl = `${API_BASE_URL}/trip-report?vehicle_id=${vehicleIds}&start_date=${formattedStartDate}&end_date=${formattedEndDate}`;
    const dataResponse = await fetch(dataUrl, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!dataResponse.ok) {
      const errorData = await dataResponse.json();
      throw new Error(errorData.Error || `HTTP error! status: ${dataResponse.status}`);
    }

    return dataResponse.json();
  };

  return useQuery({
    queryKey: ['tripReport', selectedVehicles, startDate, endDate],
    queryFn: fetchTripData,
    enabled: isEnabled && selectedVehicles.length > 0, 
    staleTime: 5 * 60 * 1000, 
  });
};


const TripReport = () => {
  // --- State Management ---
  const [searchInput, setSearchInput] = useState("");
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [dateRange, setDateRange] = useState("Last Day");
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 1), "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [reportTriggered, setReportTriggered] = useState(false); 
  const [downloadError, setDownloadError] = useState(null);

  // Autocomplete states
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const searchInputRef = useRef(null);

  // --- TanStack Query Hooks ---
  const { 
    data: availableVehicles = [], 
    isLoading: fetchingVehicles, 
    error: vehicleFetchError 
  } = useAvailableVehiclesQuery();

  const {
    data: tripData = [],
    isLoading: isTripLoading,
    error: tripFetchError,
    isRefetching: isTripRefetching,
    // manualRefetchTripData was here -> REMOVED TO FIX WARNING
  } = useTripDataQuery(selectedVehicles, startDate, endDate, reportTriggered);
  
  // Combine loading states and errors
  const loading = isTripLoading || isTripRefetching;
  const error = tripFetchError?.message || vehicleFetchError?.message || downloadError;
  const hasData = tripData.length > 0;

  // --- Effects and Handlers ---
  
  // Update suggestions whenever availableVehicles or searchInput changes
  useEffect(() => {
    const input = searchInput;
    const filtered = input.length > 0 
      ? availableVehicles.filter(vehicle => vehicle.toLowerCase().includes(input.toLowerCase()))
      : availableVehicles;
    
    setFilteredSuggestions(filtered);
  }, [searchInput, availableVehicles]);


  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchInputRef.current && !searchInputRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Search input handler
  const handleSearchInputChange = (e) => {
    const input = e.target.value;
    setSearchInput(input);
    setShowSuggestions(true);
  };

  // Suggestion click handler
  const onSuggestionClick = (vehicleId) => {
    setSearchInput("");
    setFilteredSuggestions([]);
    setShowSuggestions(false);
    if (!selectedVehicles.includes(vehicleId)) {
      setSelectedVehicles([...selectedVehicles, vehicleId]);
    }
  };

  // Date range handler
  const handleDateRangeChange = (e) => {
    const value = e.target.value;
    setDateRange(value);

    const today = new Date();
    const dateMap = {
      "Last Day": 1,
      "Last 7 Days": 7,
      "Last 30 Days": 30
    };

    if (dateMap[value]) {
      setStartDate(format(subDays(today, dateMap[value]), "yyyy-MM-dd"));
      setEndDate(format(today, "yyyy-MM-dd"));
    }
  };

  // Reset form
  const handleReset = () => {
    setSearchInput("");
    setSelectedVehicles([]);
    setDateRange("Last Day");
    setStartDate(format(subDays(new Date(), 1), "yyyy-MM-dd"));
    setEndDate(format(new Date(), "yyyy-MM-dd"));
    setReportTriggered(false); 
    setDownloadError(null);
    setFilteredSuggestions([]);
    setShowSuggestions(false);
  };

  // Trigger report fetch 
  const handleGenerateReport = () => {
    if (selectedVehicles.length > 0) {
      setReportTriggered(true);
    }
  };


  // File download handler (action, not a query)
  const handleDownload = useCallback(async (fileType) => {
    if (selectedVehicles.length === 0) {
      setDownloadError("Please select at least one vehicle to download report");
      return;
    }

    if (!hasData) {
      setDownloadError("No data available to download");
      return;
    }

    setDownloadError(null);

    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token not found");

      const vehicleIds = selectedVehicles.join(',');
      const formattedStartDate = formatDateForAPI(startDate);
      const formattedEndDate = formatDateForAPI(endDate); // USED HERE

      const url = new URL(`${API_BASE_URL}/trip-report`);
      url.searchParams.append('vehicle_id', vehicleIds);
      url.searchParams.append('start_date', formattedStartDate);
      url.searchParams.append('end_date', formattedEndDate);
      url.searchParams.append('download', fileType);

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Download failed with status ${response.status}`);
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `trip_report_${vehicleIds}_${startDate}.${fileType}`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename\*?=['"]?(?:UTF-\d['"]*)?([^;\r\n"']*)['"]?;?/i);
        if (filenameMatch && filenameMatch[1]) {
          filename = decodeURIComponent(filenameMatch[1]);
        }
      }

      // Handle file download logic
      if (fileType === 'xlsx' || fileType === 'pdf') {
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        
        const downloadLink = document.createElement('a');
        downloadLink.href = blobUrl;
        downloadLink.download = filename;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        
        // Cleanup
        setTimeout(() => {
          window.URL.revokeObjectURL(blobUrl);
          document.body.removeChild(downloadLink);
        }, 100);
      } else {
        throw new Error(`Unsupported file type: ${fileType}`);
      }

    } catch (err) {
      setDownloadError(err.message || 'Failed to download report');
      console.error("Download error:", err);
    }
    // Added endDate here to resolve the React Hook dependency warning
  }, [selectedVehicles, startDate, endDate, hasData]); 

  // Format helpers
  const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return "N/A";
    return new Date(dateTimeStr).toLocaleString();
  };

  const formatLocation = (locationStr) => {
    if (!locationStr || locationStr === "0,0") return "N/A";
    return locationStr;
  };

  // --- Skeleton Row Component ---
  const SkeletonRow = ({ columns }) => (
    <tr className="skeleton-row-trip-report">
      {[...Array(columns)].map((_, i) => (
        <td key={i}>
          <div className="skeleton-line-trip-report" style={{ width: `${Math.floor(Math.random() * (90 - 60 + 1)) + 60}%`}} />
        </td>
      ))}
    </tr>
  );

  const renderTableBody = () => {
    if (loading && reportTriggered) {
      // Show 10 skeleton rows while loading
      return (
        <>
          {[...Array(10)].map((_, index) => (
            <SkeletonRow key={index} columns={11} />
          ))}
        </>
      );
    }

    if (tripData.length > 0) {
      return tripData.map((trip, index) => (
        <tr key={index}>
          <td>{trip.TripID || "N/A"}</td>
          <td>{trip.VehicleID || "N/A"}</td>
          <td>{trip.CustomerID || "N/A"}</td>
          <td>{trip.DeviceID || "N/A"}</td>
          <td>{formatDateTime(trip.BeginTime)}</td>
          <td>{formatLocation(trip.BeginLocation)}</td>
          <td>{formatDateTime(trip.EndTime)}</td>
          <td>{formatLocation(trip.EndLocation)}</td>
          <td>{trip.TripDistance?.toFixed(2) || "N/A"}</td>
          <td>{trip.AvgSpeed?.toFixed(2) || "N/A"}</td>
          <td>{trip.MaxSpeed?.toFixed(2) || "N/A"}</td>
        </tr>
      ));
    }

    // No data or no vehicles selected
    return (
      <tr>
        <td colSpan="11" className="no-data-cell">
          {selectedVehicles.length === 0
            ? "Please select vehicles to generate report"
            : "No data available for the selected filters"}
        </td>
      </tr>
    );
  };


  return (
    <div className="trip-report-container">
      <h1 className="report-title">Trip Report</h1>
      <hr className="trip-report-line" width="100%" />

      <div className="controls-container">
        {/* Search Input */}
        <div className="search-container-trip" ref={searchInputRef}>
          <FaSearch className="search-icon-trip" />
          <input
            type="text"
            placeholder="Search Vehicle ID..."
            className="search-input-tripReport"
            value={searchInput}
            onChange={handleSearchInputChange}
            onFocus={() => {
              setFilteredSuggestions(availableVehicles);
              setShowSuggestions(true);
            }}
          />
          {showSuggestions && filteredSuggestions.length > 0 && (
            <ul className="suggestions-list">
              {filteredSuggestions.map((vehicleId, index) => (
                <li
                  key={index}
                  onClick={() => onSuggestionClick(vehicleId)}
                  className="suggestion-item"
                >
                  {vehicleId}
                </li>
              ))}
            </ul>
          )}
          {fetchingVehicles && <div className="loading-suggestions">Loading vehicles...</div>}
          {vehicleFetchError && <div className="error-message-small">Error: {vehicleFetchError.message}</div>}
        </div>

        {/* Date Controls */}
        <div className="date-range-container">
          <label className="control-label">Date Range</label>
          <select
            value={dateRange}
            onChange={handleDateRangeChange}
            className="date-range-select"
          >
            <option>Last Day</option>
            <option>Last 7 Days</option>
            <option>Last 30 Days</option>
            <option>Custom</option>
          </select>
        </div>

        <div className="date-input-container">
          <label className="control-label">Start Date:</label>
          <input
            type="date"
            className="date-input"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            disabled={dateRange !== "Custom"}
          />
        </div>

        <div className="date-input-container">
          <label className="control-label">End Date:</label>
          <input
            type="date"
            className="date-input"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            disabled={dateRange !== "Custom"}
          />
        </div>

        {/* Action Buttons */}
        <button
          className="generate-report-btn"
          onClick={handleGenerateReport}
          disabled={loading || selectedVehicles.length === 0}
        >
          {loading ? 'LOADING...' : 'GENERATE REPORT'}
        </button>

        <button className="action-btn-tripr reset-btn" onClick={handleReset}>
          RESET
        </button>
        
        <Link to="/Report">
          <button className="action-btn-tripr back-btn">
            BACK TO ALL REPORTS
          </button>
        </Link>

        {/* Export Buttons - Separate from Generate Report */}
        <FaFileExcel
          className={`excel-icon file-icon ${loading || !hasData ? 'disabled-icon' : ''}`}
          title={hasData ? "Download Excel Report" : "No data available to download"}
          onClick={() => !loading && hasData && handleDownload("xlsx")}
        />
        <FaFilePdf
          className={`pdf-icon file-icon ${loading || !hasData ? 'disabled-icon' : ''}`}
          title={hasData ? "Download PDF Report" : "No data available to download"}
          onClick={() => !loading && hasData && handleDownload("pdf")}
        />
      </div>

      {/* Selected Vehicles */}
      <div className="selected-vehicles-container">
        {selectedVehicles.length > 0 ? (
          selectedVehicles.map((vehicle, idx) => (
            <span key={idx} className="vehicle-tag">
              {vehicle}
              <FaTimes
                className="remove-icon"
                onClick={() => !loading && setSelectedVehicles(selectedVehicles.filter(v => v !== vehicle))}
                title="Remove vehicle"
              />
            </span>
          ))
        ) : (
          <div className="no-vehicles-selected">No vehicles selected</div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          Error: {error}
          {error.includes("Authentication token not found") && (
            <button
              onClick={() => window.location.href = '/login'}
              className="login-redirect-btn"
            >
              Go to Login
            </button>
          )}
        </div>
      )}

      {/* Results Table - Layout shown immediately, content is skeleton while loading */}
      <div className="table-container-trip">
        <table className="report-table-trip">
          <thead className="table-header-trip">
            <tr>
              <th>Trip ID</th>
              <th>Vehicle ID</th>
              <th>Customer ID</th>
              <th>Device ID</th>
              <th>Start Time</th>
              <th>Start Location</th>
              <th>End Time</th>
              <th>End Location</th>
              <th>Distance (km)</th>
              <th>Avg Speed (km/h)</th>
              <th>Max Speed (km/h)</th>
            </tr>
          </thead>
          <tbody>
            {renderTableBody()}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TripReport;